import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { icons } from "constants/icons";
import { getTeamsString } from "repositories/teamsApi";
import { FilterModal } from "components/FilterModal/FilterModal";
import Demographics from "./Demographics";

import { getAthleteLeaderboard } from "repositories/patientApi";
import moment from "moment";
import { AthleteModal } from "./AthletesTable";
import { getCoachTeams } from "repositories/teamsApi";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { useAppSelector } from "hooks/reduxHooks";
import { FilterType } from "constants/types";
import LeaderboardLineGraph from "./LeaderboardLineGraph";
import AthleteRanking from "./AthleteRanking";

// Updated interface for reference
const COLORS = [
  "hsl(0, 70%, 50%)",
  "hsl(131, 92.82868525896416%, 49.2156862745098%)",
  "hsl(240, 70%, 50%)",
  "hsl(180, 70%, 50%)",
  "hsl(120, 70%, 50%)",
];

interface Athlete {
  _id: string;
  firstName: string;
  lastName: string;
  avatar: string;
  rank: string;
  age: number;
  teams: string[];
  positions: string[];
  sports: string[];
  kneeMetric: number;
  lastExam: string;
  calculations: {
    currentAverage: number;
    previousAverage: number;
    averagesDiffPercentage: number;
    hasImproved: boolean;
  };
  leftSideData: {
    current: {
      leftMinValue: number;
    };
    previous: {
      leftMinValue: number;
    };
  };
  rightSideData: {
    current: {
      rightMinValue: number;
    };
    previous: {
      rightMinValue: number;
    };
  };
  bothSideData: {
    current: {
      leftMinValue: number;
      rightMinValue: number;
    };
    previous: {
      leftMinValue: number;
      rightMinValue: number;
    };
  };

  patient: {
    _id: string;
    imageUrl: string;
    profileInfo: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      sports: string[];
      teams: string[];
      gender: string;
      sportPosition: string[];
    };
  };
}

const LeaderboardPage: React.FC = () => {
  const [athleteList, setAthleteList] = useState<Athlete[]>([]);
  const [worstAthleteList, setWorstAthleteList] = useState<Athlete[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [fullList, setFullList] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [worstFullList, setWorstFullList] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [demographics, setDemographics] = useState<any[]>([]);
  const currentClinician: any = useAppSelector(selectCurrentClinician);
  const history = useHistory();

  const getLeaderboard = async (filters?: any) => {
    setLoading(true);
    try {
      const topAthletesReq = getAthleteLeaderboard(filters);
      const bottomAthletesReq = getAthleteLeaderboard(filters, true);
      const [topAthletes, bottomAthletes] = await Promise.all([
        topAthletesReq,
        bottomAthletesReq,
      ]);

      if (topAthletes.items.length > 0) {
        setAthleteList(topAthletes.items.slice(0, 5));
        setFullList({
          total: topAthletes.items.length,
          items: topAthletes.items.map((item: any, i: number) => {
            return {
              ...item,
              patient: {
                ...item.patient,
                rank: i + 1,
              },
            };
          }),
        });
      }

      if (bottomAthletes.items.length > 0) {
        const sortedListWorst = Array.from(bottomAthletes.items)
          .sort(
            (a: any, b: any) =>
              (a.calculations.currentAverage || 0) -
              (b.calculations.currentAverage || 0)
          )
          .map((item: any, i: number) => {
            return {
              ...item,
              patient: {
                ...item.patient,
                rank: i + 1,
              },
            };
          });

        setWorstAthleteList(sortedListWorst.slice(0, 5));
        setWorstFullList({
          total: bottomAthletes.items.length,
          items: sortedListWorst,
        });
      }

      const demographics: any[] = [];
      Object.entries(topAthletes.totalAthletesDemographics).map(
        (arr: any, index: number) => {
          const [key, value] = arr;
          demographics.push({
            id: key,
            label: key,
            value: value,
            color: COLORS[index],
          });
        }
      );

      setDemographics(demographics);

      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLeaderboard();
  }, []);

  const getTeams = async () => {
    if (!currentClinician) return;
    try {
      const fetchedTeams = await getCoachTeams(currentClinician._id);

      setTeams(fetchedTeams);
    } catch (error) {}
  };

  useEffect(() => {
    getTeams();
  }, []);

  return (
    <div className="p-3 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl flex items-baseline gap-2">
          <span
            className="font-black"
            style={{
              fontFamily: "Sofia Pro",
            }}
          >
            Report overview
          </span>
          <span className="font-light text-gray-700">- Comparative list</span>
          <span className="font-light text-gray-500">
            | {moment().format("MM/DD/YY")}
          </span>
        </h1>
      </div>

      <hr className="mb-5" />

      <div className="flex items-center gap-2 mb-6">
        <h2 className="text-xl font-bold">Athlete list</h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <img
            aria-hidden="true"
            src={icons.filterDetail}
            alt="filterDetail"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setOpenModal(true);
            }}
          />
        </div>
      </div>

      <AthleteModal
        setOpenFilters={setOpenModal}
        athletes={fullList.items}
        totalCount={fullList.total}
        withPagination={true}
      />
      <AthleteRanking
        section="top"
        loading={loading}
        label={"Top 5 performance athlete list"}
        athleteList={athleteList}
        teams={teams}
      />

      <LeaderboardLineGraph athleteList={athleteList} />
      <hr style={{ marginTop: "10px", marginBottom: "10px" }} />
      <Demographics
        loading={loading}
        topFive={athleteList}
        demographics={demographics}
      />
      <hr style={{ marginTop: "10px", marginBottom: "10px" }} />

      <AthleteModal
        setOpenFilters={setOpenModal}
        athletes={worstFullList.items}
        totalCount={worstFullList.total}
        withPagination={true}
      />
      <AthleteRanking
        section="bottom"
        loading={loading}
        label={"Opportunity for athletic improvement"}
        athleteList={worstAthleteList}
        teams={teams}
      />

      <LeaderboardLineGraph athleteList={worstAthleteList} />
      <FilterModal
        customConfirm={(data: any) => {
          getLeaderboard(data);
        }}
        customReset={() => {
          getLeaderboard();
        }}
        fromSection="LEADERBOARD"
        isEnabled={openModal}
        setIsEnabled={setOpenModal}
        operation={FilterType.ATHLETE_FILTER}
        currentClinician={currentClinician}
        hideInputs={["examTypes", "minValue"]}
        applyFilter={() => {}}
        tags={[]}
        inputStyle={{
          border: "1px",
          height: "34px",
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
          borderColor: "#E5E5E5",
          fontSize: "14px",
          color: "#5C5D69",
        }}
        onItemSelected={() => {}}
        resetFilter={() => {}}
      />
    </div>
  );
};

export default LeaderboardPage;
